/**
 * Dev Mode — frontend overlay for debugging animation lifecycles, scope
 * detection, and event firing. Toggle with the `D` key (or `?lay-dev=1`
 * URL param + `D`). Admin-gated unless the URL param is present.
 *
 * Phase 1 (this file as initially shipped):
 *   - Activation gating
 *   - D-key toggle + URL-param opt-in
 *   - Empty overlay shell with title + close button
 *
 * Later phases will plug collectors into this shell — see TODO blocks.
 *
 * Performance: zero overhead when the overlay is closed. Collectors and
 * the render loop only run while `state.open === true`.
 */

const PANEL_ID = 'lay-dev-mode-overlay'
const STYLE_ID = 'lay-dev-mode-styles'
const URL_PARAM = 'lay-dev'
// Sticky toggle: writing this to localStorage opens the overlay
// automatically on every subsequent page load, until the user closes it
// explicitly (which clears the flag).
const PERSIST_KEY = 'lay-dev-mode-open_v1'
// Sticky position: `{ left, top }` (px from viewport top-left). Persists
// where the user dragged the panel last so it stays put across reloads.
const POSITION_KEY = 'lay-dev-mode-position_v1'
// Sticky fullscreen toggle: `"1"` while the panel is expanded to fill
// the viewport. Survives reloads so debugging sessions don't reset.
const FULLSCREEN_KEY = 'lay-dev-mode-fullscreen_v1'
const VIEWPORT_MARGIN = 4 // keep at least this many px on screen
// Log buffer cap — ring-buffer so a long debug session can't grow the
// memory footprint forever. 50 covers ~10s of real activity.
const EVENT_LOG_MAX = 50
// Render at 4 Hz: snappy enough that counters look live without
// hammering the main thread.
const RENDER_INTERVAL_MS = 250

// One global state object — keeps the module hot-reload friendly and
// avoids closing over per-init state in event handlers we attach to
// window listeners.
const state = {
	enabled: false,
	open: false,
	panel: null,
	renderTimer: null,
	bodyHovered: false,
	collectorsInstalled: false,
	fullscreen: false,
	// Persistent-DOM render state: refs to specific cells that get
	// `textContent` updates each tick, plus a Map of event-name → row
	// for incremental table updates. Building DOM once and mutating in
	// place is the only way to keep scroll position / text selection /
	// pointer interactions stable during a 4 Hz render loop.
	refs: null,
	eventRows: null,
	lastLogT: 0,
	// Collector store: counters + log + last lifecycle phase.
	// Counts are split by scope (page / project overlay / page overlay)
	// because the three scopes can coexist (a page overlay can be visible
	// on top of a project overlay on top of a page) and the same event
	// name fires in each. Per-scope counting lets us spot "this fires
	// twice when overlays are stacked" type issues.
	events: new Map(),
	log: [],
	lifecyclePhase: 'idle',
	collectorStartedAt: 0,
	// Physics-body visualiser. When enabled, an absolute-positioned
	// overlay div on document.body draws outlines for every currently-
	// bound drag-physics body, on every RAF, so you can see where the
	// body's collision shape actually is (vs. the visible element rect).
	// Useful for diagnosing line-rect vs element-rect mismatch on text
	// physics — the body is the red box, the visible element is yellow.
	physicsOverlayEnabled: false,
	physicsOverlayEl: null,
	physicsOverlayRaf: 0,
}

const PHYSICS_OVERLAY_ID = 'lay-dev-mode-physics-overlay'
const PHYSICS_OVERLAY_KEY = 'lay-dev-mode-physics-overlay_v1'

function isActivationAllowed() {
	if (typeof window === 'undefined') return false
	// `wp_localize_script` serialises PHP booleans as the strings "1" /
	// "" (not `true` / `false`), so accept any truthy value here rather
	// than a strict `=== true`.
	const adminFlag = window.layData && window.layData.isAdmin
	if (adminFlag === true || adminFlag === '1' || adminFlag === 1) return true
	// Non-admin opt-in: `?lay-dev=1` for one-off debug sessions on a
	// staging machine where the test account doesn't have manage_options.
	try {
		const params = new URLSearchParams(window.location.search)
		if (params.get(URL_PARAM) === '1') return true
	} catch (_) {
		// `URLSearchParams` is in every modern browser; ignore env where
		// it isn't available.
	}
	return false
}

// ─── Scope detection ─────────────────────────────────────────────────
// Mirrored (intentionally duplicated, ~5 lines each) from
// `drag_physics_controller.js`. Keeping these local means the dev-mode
// module is fully standalone — no cross-module import for tiny helpers
// that are stable in shape.

function hasVisibleProjectOverlay() {
	const overlay = document.getElementById('lay-project-overlay')
	if (!overlay) return false
	const style = window.getComputedStyle(overlay)
	if (style.display === 'none' || style.visibility === 'hidden') return false
	return overlay.querySelector('.lay-content') !== null
}

function hasVisiblePageOverlay() {
	return document.querySelector('.lay-overlay.lay-show-overlay, .lay-overlay[style*="display: block"]') !== null
}

function detectScopes() {
	const project = hasVisibleProjectOverlay()
	const pageOv = hasVisiblePageOverlay()
	return {
		// "page" = the underlying document is the topmost active surface
		// (no overlay shadowing it). When an overlay is visible, the
		// page is still in the DOM but the user isn't interacting with
		// it; we don't count events as "page" in that case so the
		// counter reads as "events that fired when the bare page was on
		// screen".
		page: !project && !pageOv,
		projectOverlay: project,
		pageOverlay: pageOv,
	}
}

/**
 * Infer the SCOPE an event belongs to, NOT just "which overlay is
 * visible right now". Many laytheme events carry their scope inline
 *  — either in the event name (`project_overlay_open_start`) or as
 *  string args (`newpage(layoutObj, 'project', …)`,
 *  `navigation_in_start('project_overlay')`). Using that signal is
 *  much more accurate than the visibility check because when a
 *  project overlay opens, the *underlying* page also continues to
 *  fire events — and naive visibility-check would mis-tag them as
 *  overlay events.
 *
 *  Returns at least one true flag. Falls back to `detectScopes()`
 *  when nothing inferable is found.
 */
function inferEventScopes(name, args) {
	// 1. Event-name hints (highest confidence).
	if (typeof name === 'string') {
		if (name.indexOf('project_overlay') !== -1) {
			return { page: false, projectOverlay: true, pageOverlay: false }
		}
		if (name.indexOf('page_overlay') !== -1) {
			return { page: false, projectOverlay: false, pageOverlay: true }
		}
	}

	// 2. String-arg hints. Many emits put the scope kind into one of
	//    the first few args using a small set of well-known tokens.
	if (args && args.length > 0) {
		for (let i = 0; i < args.length && i < 4; i++) {
			const a = args[i]
			if (typeof a !== 'string') continue
			if (a === 'project' || a === 'project_overlay' || a === 'projects') {
				return { page: false, projectOverlay: true, pageOverlay: false }
			}
			if (a === 'page_overlay') {
				return { page: false, projectOverlay: false, pageOverlay: true }
			}
			if (a === 'page' || a === 'normal') {
				return { page: true, projectOverlay: false, pageOverlay: false }
			}
		}
	}

	// 3. Fallback: tag with whatever's visually on top right now. Not
	//    100% reliable (see above) but the best we can do for events
	//    that carry no inline scope info (`removeListener`, internal
	//    bookkeeping, etc.).
	return detectScopes()
}

function isPhoneSize() {
	if (window.utility && typeof window.utility.isPhoneSize === 'function') {
		return window.utility.isPhoneSize()
	}
	const bp = Number(window.layData && (window.layData.breakpoint || window.layData.phone_breakpoint))
	return window.innerWidth <= (Number.isFinite(bp) && bp > 0 ? bp : 600)
}

// ─── Collectors ──────────────────────────────────────────────────────

function summariseArgs(args) {
	if (!args || args.length === 0) return ''
	try {
		const parts = []
		for (let i = 0; i < args.length && i < 4; i++) {
			const a = args[i]
			if (a === null || a === undefined) parts.push(String(a))
			else if (typeof a === 'object') parts.push(Array.isArray(a) ? `[${a.length}]` : 'obj')
			else parts.push(String(a).slice(0, 40))
		}
		return parts.join(', ')
	} catch (_) {
		return ''
	}
}

function recordEvent(name, args) {
	const scopes = inferEventScopes(name, args)
	const now = performance.now()
	let entry = state.events.get(name)
	if (!entry) {
		entry = {
			counts: { page: 0, projectOverlay: 0, pageOverlay: 0, total: 0 },
			lastT: 0,
			lastDetail: '',
		}
		state.events.set(name, entry)
	}
	entry.counts.total += 1
	if (scopes.page) entry.counts.page += 1
	if (scopes.projectOverlay) entry.counts.projectOverlay += 1
	if (scopes.pageOverlay) entry.counts.pageOverlay += 1
	entry.lastT = now
	entry.lastDetail = summariseArgs(args)

	// Compact scope tag for the log column.
	const scopeTag = scopes.projectOverlay ? 'projOv' : scopes.pageOverlay ? 'pageOv' : 'page'
	state.log.push({ t: now, scope: scopeTag, event: name, detail: entry.lastDetail })
	if (state.log.length > EVENT_LOG_MAX) state.log.shift()
}

function installCollectors() {
	if (state.collectorsInstalled) return
	state.collectorsInstalled = true
	state.collectorStartedAt = performance.now()

	// Monkey-patch `window.laytheme.emit` so every emitted event is
	// captured automatically — no need to enumerate / whitelist event
	// names. `events_channels.js` runs in HEAD (before `app.js`), so
	// `window.laytheme` already exists when this init runs.
	const lay = window.laytheme
	if (lay && typeof lay.emit === 'function') {
		const original = lay.emit.bind(lay)
		lay.emit = function (name, ...args) {
			try { recordEvent(name, args) } catch (_) {}
			return original(name, ...args)
		}
	}

	// Animation-lifecycle phase changes fire as a CustomEvent on window
	// (separate channel from laytheme.emit).
	window.addEventListener('lay:animation_lifecycle_navigation_phase_changed', (e) => {
		try {
			const phase = e && e.detail && e.detail.phase
			if (typeof phase === 'string') state.lifecyclePhase = phase
			recordEvent('lifecycle:navigation_phase_changed', [phase])
		} catch (_) {}
	})
}

function isTypingTarget(target) {
	if (!target || typeof target !== 'object') return false
	if (target.isContentEditable) return true
	const tag = target.tagName
	if (!tag) return false
	return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

function injectStyles() {
	if (document.getElementById(STYLE_ID)) return
	const style = document.createElement('style')
	style.id = STYLE_ID
	// Inline CSS keeps the module self-contained: no separate stylesheet
	// to enqueue and no risk of theme styles bleeding into the overlay.
	// Sized + coloured to read like a developer console rather than
	// part of the visible page chrome.
	style.textContent = `
		#${PANEL_ID}.is-fullscreen {
			top: 0 !important;
			left: 0 !important;
			right: 0 !important;
			width: 100vw !important;
			height: 100vh !important;
			max-height: 100vh !important;
			border-radius: 0;
		}
		#${PANEL_ID}.is-fullscreen .lay-dev-mode-header {
			cursor: default;
		}
		#${PANEL_ID}.is-fullscreen pre.lay-dev-mode-log {
			max-height: none;
			flex: 1 1 auto;
		}
		#${PANEL_ID} {
			position: fixed;
			top: 12px;
			right: 12px;
			width: 380px;
			max-height: calc(100vh - 24px);
			z-index: 2147483647;
			background: rgba(20, 20, 22, 0.92);
			color: #e8e8ea;
			font-family: -apple-system, ui-monospace, SFMono-Regular, Menlo, monospace;
			font-size: 11px;
			line-height: 1.4;
			border-radius: 6px;
			box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
			display: flex;
			flex-direction: column;
			overflow: hidden;
			pointer-events: auto;
			user-select: text;
		}
		#${PANEL_ID} .lay-dev-mode-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 8px 10px;
			background: rgba(0, 0, 0, 0.35);
			border-bottom: 1px solid rgba(255, 255, 255, 0.08);
			flex: 0 0 auto;
			cursor: grab;
			user-select: none;
		}
		#${PANEL_ID} .lay-dev-mode-header.is-dragging {
			cursor: grabbing;
		}
		#${PANEL_ID} .lay-dev-mode-header.is-dragging * {
			cursor: grabbing !important;
		}
		#${PANEL_ID} .lay-dev-mode-title {
			font-weight: 600;
			letter-spacing: 0.04em;
		}
		#${PANEL_ID} .lay-dev-mode-header-actions {
			display: flex;
			gap: 4px;
			align-items: center;
		}
		#${PANEL_ID} .lay-dev-mode-btn {
			background: transparent;
			border: 0;
			color: inherit;
			cursor: pointer;
			padding: 3px 8px;
			font: inherit;
			font-size: 10px;
			line-height: 1;
			border-radius: 3px;
		}
		#${PANEL_ID} .lay-dev-mode-btn:hover {
			background: rgba(255, 255, 255, 0.08);
		}
		#${PANEL_ID} .lay-dev-mode-btn.is-flash {
			background: rgba(80, 180, 100, 0.35);
		}
		#${PANEL_ID} .lay-dev-mode-btn.active {
			background: rgba(255, 51, 51, 0.25);
			border-color: rgba(255, 51, 51, 0.6);
		}
		#${PANEL_ID} .lay-dev-mode-close {
			background: transparent;
			border: 0;
			color: inherit;
			cursor: pointer;
			padding: 2px 6px;
			font-size: 14px;
			line-height: 1;
			border-radius: 3px;
		}
		#${PANEL_ID} .lay-dev-mode-close:hover {
			background: rgba(255, 255, 255, 0.08);
		}
		#${PANEL_ID} .lay-dev-mode-body {
			padding: 10px;
			overflow-y: auto;
			flex: 1 1 auto;
		}
		#${PANEL_ID} .lay-dev-mode-placeholder {
			color: rgba(255, 255, 255, 0.45);
			font-style: italic;
		}
		#${PANEL_ID} .lay-dev-mode-section {
			margin-bottom: 12px;
		}
		#${PANEL_ID} .lay-dev-mode-section:last-child {
			margin-bottom: 0;
		}
		#${PANEL_ID} .lay-dev-mode-h {
			font-size: 9px;
			text-transform: uppercase;
			letter-spacing: 0.08em;
			color: rgba(255, 255, 255, 0.5);
			margin-bottom: 4px;
		}
		#${PANEL_ID} .lay-dev-mode-row {
			display: flex;
			justify-content: space-between;
			gap: 8px;
		}
		#${PANEL_ID} .lay-dev-mode-row span:last-child {
			color: rgba(255, 255, 255, 0.75);
		}
		#${PANEL_ID} table.lay-dev-mode-table {
			width: 100%;
			border-collapse: collapse;
			font-size: 10px;
		}
		#${PANEL_ID} table.lay-dev-mode-table th,
		#${PANEL_ID} table.lay-dev-mode-table td {
			padding: 2px 4px;
			text-align: right;
			border-bottom: 1px solid rgba(255, 255, 255, 0.05);
			white-space: nowrap;
		}
		#${PANEL_ID} table.lay-dev-mode-table th:first-child,
		#${PANEL_ID} table.lay-dev-mode-table td:first-child {
			text-align: left;
			max-width: 140px;
			overflow: hidden;
			text-overflow: ellipsis;
		}
		#${PANEL_ID} table.lay-dev-mode-table th {
			color: rgba(255, 255, 255, 0.4);
			font-weight: 500;
		}
		#${PANEL_ID} pre.lay-dev-mode-log {
			margin: 0;
			max-height: 240px;
			overflow-y: auto;
			font-size: 10px;
			line-height: 1.35;
			color: rgba(255, 255, 255, 0.8);
			white-space: pre;
		}
		#${PANEL_ID} .lay-dev-mode-log-entry {
			white-space: pre;
		}
	`
	document.head.appendChild(style)
}

function buildPanel() {
	if (state.panel) return state.panel
	const panel = document.createElement('div')
	panel.id = PANEL_ID
	panel.setAttribute('role', 'region')
	panel.setAttribute('aria-label', 'Lay Dev Mode')
	// The theme uses Lenis smooth-scroll, which captures every wheel /
	// touchmove on `window` and translates the page. Without an opt-out,
	// wheeling over our log would scroll the page underneath at the
	// same time. Lenis's built-in opt-out is the `data-lenis-prevent`
	// attribute — wheel events from inside this subtree get ignored.
	panel.setAttribute('data-lenis-prevent', '1')
	// Persistent skeleton: built ONCE, then individual cells/spans get
	// `textContent` updates on every render tick. No `innerHTML` swaps
	// on big containers — that's what was breaking scroll, selection,
	// and clicking on log rows mid-render.
	panel.innerHTML = `
		<div class="lay-dev-mode-header">
			<span class="lay-dev-mode-title">LAY DEV MODE</span>
			<div class="lay-dev-mode-header-actions">
				<button type="button" class="lay-dev-mode-btn lay-dev-mode-physics-bodies" title="Draw outlines of drag-physics body bounds, compound parts, restrain area">Physics</button>
				<button type="button" class="lay-dev-mode-btn lay-dev-mode-fullscreen" title="Expand to fill the viewport">Fullscreen</button>
				<button type="button" class="lay-dev-mode-btn lay-dev-mode-copy" title="Copy panel contents to clipboard">Copy</button>
				<button type="button" class="lay-dev-mode-btn lay-dev-mode-clear" title="Reset event counters and log">Clear</button>
				<button type="button" class="lay-dev-mode-close" aria-label="Close" title="Close (D)">×</button>
			</div>
		</div>
		<div class="lay-dev-mode-body">
			<div class="lay-dev-mode-section">
				<div class="lay-dev-mode-h">Context</div>
				<div class="lay-dev-mode-row"><span>Layout</span><span data-field="layout">—</span></div>
				<div class="lay-dev-mode-row"><span>Scope</span><span data-field="scope">—</span></div>
				<div class="lay-dev-mode-row"><span>Nav phase</span><span data-field="navPhase">—</span></div>
			</div>
			<div class="lay-dev-mode-section">
				<div class="lay-dev-mode-h" data-field="countsHeader">Event counts (0)</div>
				<div class="lay-dev-mode-placeholder" data-field="countsEmpty">No events yet</div>
				<table class="lay-dev-mode-table" data-field="countsTable" style="display:none">
					<thead><tr><th>Event</th><th title="Page">P</th><th title="Project overlay">Pr</th><th title="Page overlay">Pg</th><th>Σ</th><th title="Timestamp of the most recent fire (seconds since collector start)">At</th></tr></thead>
					<tbody data-field="countsBody"></tbody>
				</table>
			</div>
			<div class="lay-dev-mode-section">
				<div class="lay-dev-mode-h" data-field="logHeader">Event log (0)</div>
				<div class="lay-dev-mode-placeholder" data-field="logEmpty">No events yet</div>
				<pre class="lay-dev-mode-log" data-field="log" style="display:none"></pre>
			</div>
		</div>
	`
	// Cache references to the cells that change every tick. Doing this
	// once at build time keeps the render loop free of query selectors.
	state.refs = {
		layout: panel.querySelector('[data-field="layout"]'),
		scope: panel.querySelector('[data-field="scope"]'),
		navPhase: panel.querySelector('[data-field="navPhase"]'),
		countsHeader: panel.querySelector('[data-field="countsHeader"]'),
		countsEmpty: panel.querySelector('[data-field="countsEmpty"]'),
		countsTable: panel.querySelector('[data-field="countsTable"]'),
		countsBody: panel.querySelector('[data-field="countsBody"]'),
		logHeader: panel.querySelector('[data-field="logHeader"]'),
		logEmpty: panel.querySelector('[data-field="logEmpty"]'),
		log: panel.querySelector('[data-field="log"]'),
	}
	state.eventRows = new Map()
	state.lastLogT = 0
	const closeBtn = panel.querySelector('.lay-dev-mode-close')
	if (closeBtn) {
		closeBtn.addEventListener('click', () => setOpen(false))
	}
	const copyBtn = panel.querySelector('.lay-dev-mode-copy')
	if (copyBtn) {
		copyBtn.addEventListener('click', () => handleCopyClick(copyBtn))
	}
	const clearBtn = panel.querySelector('.lay-dev-mode-clear')
	if (clearBtn) {
		clearBtn.addEventListener('click', () => {
			state.events.clear()
			state.log.length = 0
			state.collectorStartedAt = performance.now()
			state.lastLogT = 0
			// Persistent-DOM bookkeeping: drop the existing row refs
			// + clear the table body so renderBody can rebuild rows
			// for whatever events fire next.
			if (state.eventRows) state.eventRows.clear()
			if (state.refs) {
				if (state.refs.countsBody) state.refs.countsBody.textContent = ''
				if (state.refs.log) state.refs.log.textContent = ''
			}
			renderBody()
		})
	}
	const header = panel.querySelector('.lay-dev-mode-header')
	if (header) installHeaderDrag(panel, header)
	const physicsBtn = panel.querySelector('.lay-dev-mode-physics-bodies')
	if (physicsBtn) {
		physicsBtn.addEventListener('click', () => setPhysicsOverlayEnabled(!state.physicsOverlayEnabled))
		updatePhysicsButton(physicsBtn)
	}
	const fullscreenBtn = panel.querySelector('.lay-dev-mode-fullscreen')
	if (fullscreenBtn) {
		fullscreenBtn.addEventListener('click', () => setFullscreen(!state.fullscreen))
	}

	state.panel = panel
	return panel
}

function readPersistedFullscreen() {
	try {
		return window.localStorage.getItem(FULLSCREEN_KEY) === '1'
	} catch (_) {
		return false
	}
}

function writePersistedFullscreen(value) {
	try {
		if (value) window.localStorage.setItem(FULLSCREEN_KEY, '1')
		else window.localStorage.removeItem(FULLSCREEN_KEY)
	} catch (_) {}
}

function setFullscreen(next) {
	state.fullscreen = next
	if (state.panel) {
		state.panel.classList.toggle('is-fullscreen', next)
		const btn = state.panel.querySelector('.lay-dev-mode-fullscreen')
		if (btn) btn.textContent = next ? 'Compact' : 'Fullscreen'
		if (!next) {
			// On exit, restore the user's last dragged position so the
			// panel snaps back to where they left it. The is-fullscreen
			// class removal lifts the `!important` overrides; existing
			// `left/top` inline style (set by `applyPanelPosition`)
			// takes over again.
			const stored = readPersistedPosition()
			if (stored) applyPanelPosition(state.panel, stored.left, stored.top)
		}
	}
	writePersistedFullscreen(next)
}

function buildSnapshotText() {
	const scopes = detectScopes()
	const phone = isPhoneSize()
	const now = performance.now()
	const lines = []
	lines.push('LAY DEV MODE — ' + (typeof window !== 'undefined' ? window.location.href : ''))
	lines.push('Captured ' + new Date().toISOString())
	lines.push('='.repeat(70))
	lines.push('')
	lines.push('Context')
	lines.push('-------')
	lines.push(`Layout:    ${phone ? 'Phone' : 'Desktop'} (${window.innerWidth}px)`)
	const activeScopes = []
	if (scopes.page) activeScopes.push('page')
	if (scopes.projectOverlay) activeScopes.push('project-overlay')
	if (scopes.pageOverlay) activeScopes.push('page-overlay')
	lines.push(`Scope:     ${activeScopes.join(' + ') || '—'}`)
	lines.push(`Nav phase: ${state.lifecyclePhase}`)
	lines.push('')

	lines.push('Event counts (' + state.events.size + ')')
	lines.push('-------------')
	if (state.events.size === 0) {
		lines.push('(none)')
	} else {
		// Pad columns so the text is greppable.
		const entries = Array.from(state.events.entries()).sort((a, b) => b[1].lastT - a[1].lastT)
		const nameWidth = Math.max(...entries.map(([n]) => n.length), 'event'.length)
		lines.push(
			'event'.padEnd(nameWidth) + '  ' +
			'P'.padStart(4) + '  ' +
			'Pr'.padStart(4) + '  ' +
			'Pg'.padStart(4) + '  ' +
			'Σ'.padStart(5) + '  ' +
			'at'.padStart(8)
		)
		for (const [name, e] of entries) {
			const at = ((e.lastT - state.collectorStartedAt) / 1000).toFixed(2) + 's'
			lines.push(
				name.padEnd(nameWidth) + '  ' +
				String(e.counts.page).padStart(4) + '  ' +
				String(e.counts.projectOverlay).padStart(4) + '  ' +
				String(e.counts.pageOverlay).padStart(4) + '  ' +
				String(e.counts.total).padStart(5) + '  ' +
				at.padStart(8)
			)
		}
	}
	lines.push('')

	lines.push('Event log (most recent first, last ' + state.log.length + ')')
	lines.push('---------')
	if (state.log.length === 0) {
		lines.push('(none)')
	} else {
		const recent = state.log.slice().reverse()
		for (const r of recent) {
			const t = ((r.t - state.collectorStartedAt) / 1000).toFixed(2).padStart(7, ' ')
			const scope = (r.scope + '      ').slice(0, 7)
			lines.push(`${t}s ${scope} ${r.event}${r.detail ? '  ' + r.detail : ''}`)
		}
	}

	return lines.join('\n')
}

function handleCopyClick(btn) {
	const text = buildSnapshotText()
	const flash = () => {
		btn.classList.add('is-flash')
		const original = btn.textContent
		btn.textContent = 'Copied'
		window.setTimeout(() => {
			btn.classList.remove('is-flash')
			btn.textContent = original
		}, 900)
	}
	// Prefer the async clipboard API; fall back to the textarea-select-
	// execCommand trick for older browsers + non-secure-origin staging.
	if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
		navigator.clipboard.writeText(text).then(flash).catch(() => fallbackCopy(text, flash))
	} else {
		fallbackCopy(text, flash)
	}
}

function fallbackCopy(text, onOk) {
	try {
		const ta = document.createElement('textarea')
		ta.value = text
		ta.style.position = 'fixed'
		ta.style.left = '-9999px'
		document.body.appendChild(ta)
		ta.focus()
		ta.select()
		const ok = document.execCommand('copy')
		document.body.removeChild(ta)
		if (ok && typeof onOk === 'function') onOk()
	} catch (_) {
		// give up silently — user can read the panel directly
	}
}

function escapeHtml(s) {
	return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}

function renderBody() {
	const refs = state.refs
	if (!refs) return

	const scopes = detectScopes()
	const phone = isPhoneSize()
	const now = performance.now()

	// ── Context (just textContent updates — no DOM churn) ────────────
	refs.layout.textContent = `${phone ? 'Phone' : 'Desktop'} (${window.innerWidth}px)`
	const activeScopes = []
	if (scopes.page) activeScopes.push('page')
	if (scopes.projectOverlay) activeScopes.push('project-overlay')
	if (scopes.pageOverlay) activeScopes.push('page-overlay')
	refs.scope.textContent = activeScopes.join(' + ') || '—'
	refs.navPhase.textContent = state.lifecyclePhase

	// ── Event counters ──────────────────────────────────────────────
	refs.countsHeader.textContent = `Event counts (${state.events.size})`
	if (state.events.size === 0) {
		refs.countsEmpty.style.display = ''
		refs.countsTable.style.display = 'none'
	} else {
		refs.countsEmpty.style.display = 'none'
		refs.countsTable.style.display = ''
		// Append a row for any new event names we've never seen. Then
		// update each existing row's cells in place. No removal — the
		// "Clear" button is what drops rows (handled by resetting
		// `state.eventRows` + clearing tbody innerHTML).
		for (const [name, e] of state.events) {
			let row = state.eventRows.get(name)
			if (!row) {
				const tr = document.createElement('tr')
				const nameTd = document.createElement('td')
				nameTd.textContent = name
				nameTd.title = name
				const cP = document.createElement('td')
				const cPr = document.createElement('td')
				const cPg = document.createElement('td')
				const cT = document.createElement('td')
				const cAt = document.createElement('td')
				tr.appendChild(nameTd)
				tr.appendChild(cP)
				tr.appendChild(cPr)
				tr.appendChild(cPg)
				tr.appendChild(cT)
				tr.appendChild(cAt)
				refs.countsBody.appendChild(tr)
				row = { tr: tr, cP: cP, cPr: cPr, cPg: cPg, cT: cT, cAt: cAt }
				state.eventRows.set(name, row)
			}
			row.cP.textContent = String(e.counts.page)
			row.cPr.textContent = String(e.counts.projectOverlay)
			row.cPg.textContent = String(e.counts.pageOverlay)
			row.cT.textContent = String(e.counts.total)
			// Fixed timestamp: seconds since the collector started. Only
			// changes when the event next fires (entry.lastT updates),
			// so the column doesn't tick every render — the user
	    		// previously complained about that being distracting.
			row.cAt.textContent = ((e.lastT - state.collectorStartedAt) / 1000).toFixed(2) + 's'
		}
	}

	// ── Event log (per-entry DOM, append-only, no innerHTML rebuilds) ──
	// Each entry is its own child <div>. New entries append to the end
	// (chronological, like a console log — newest at the bottom). We
	// only ever touch the LAST appended children's parent, so the
	// scrollTop of the log container stays stable while the user
	// reads earlier entries. When the cap is exceeded, the oldest
	// children get removed from the front — removing nodes above the
	// current scroll position doesn't disturb visible content.
	refs.logHeader.textContent = `Event log (${state.log.length})`
	if (state.log.length === 0) {
		refs.logEmpty.style.display = ''
		refs.log.style.display = 'none'
	} else {
		refs.logEmpty.style.display = 'none'
		refs.log.style.display = ''
		// Auto-tail detection BEFORE we append: if the user was at the
		// bottom (within 4 px of scrollHeight), keep them pinned to
		// the bottom after the append. Otherwise leave their scroll
		// position alone so reading older entries isn't interrupted.
		const wasAtBottom = refs.log.scrollHeight - refs.log.clientHeight - refs.log.scrollTop < 4

		// Append entries newer than the watermark. Using a watermark
		// `lastLogT` (timestamp of the last rendered entry) handles
		// the case where state.log gets shifted (cap-overflow at the
		// collector level) without index drift.
		for (let i = 0; i < state.log.length; i++) {
			const r = state.log[i]
			if (r.t <= state.lastLogT) continue
			const t = ((r.t - state.collectorStartedAt) / 1000).toFixed(2).padStart(7, ' ')
			const scope = (r.scope + '      ').slice(0, 7)
			const line = `${t}s ${scope} ${r.event}${r.detail ? '  ' + r.detail : ''}`
			const div = document.createElement('div')
			div.className = 'lay-dev-mode-log-entry'
			div.textContent = line
			refs.log.appendChild(div)
		}
		// Update watermark to the last-appended entry's timestamp.
		state.lastLogT = state.log[state.log.length - 1].t

		// Cap: drop oldest children once we exceed the line budget.
		while (refs.log.childElementCount > EVENT_LOG_MAX) {
			refs.log.removeChild(refs.log.firstChild)
		}

		if (wasAtBottom) {
			refs.log.scrollTop = refs.log.scrollHeight
		}
	}
}

function startRenderLoop() {
	if (state.renderTimer) return
	renderBody()
	state.renderTimer = window.setInterval(() => {
		if (!state.open) {
			stopRenderLoop()
			return
		}
		renderBody()
	}, RENDER_INTERVAL_MS)
}

function stopRenderLoop() {
	if (state.renderTimer) {
		window.clearInterval(state.renderTimer)
		state.renderTimer = null
	}
}

function readPersistedOpen() {
	try {
		return window.localStorage.getItem(PERSIST_KEY) === '1'
	} catch (_) {
		return false
	}
}

function writePersistedOpen(value) {
	try {
		if (value) {
			window.localStorage.setItem(PERSIST_KEY, '1')
		} else {
			window.localStorage.removeItem(PERSIST_KEY)
		}
	} catch (_) {
		// Quota / disabled storage — ignore. Overlay still works in-session.
	}
}

function readPersistedPosition() {
	try {
		const raw = window.localStorage.getItem(POSITION_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed.left === 'number' && typeof parsed.top === 'number') return parsed
		return null
	} catch (_) {
		return null
	}
}

function writePersistedPosition(left, top) {
	try {
		window.localStorage.setItem(POSITION_KEY, JSON.stringify({ left: left, top: top }))
	} catch (_) {
		// ignore
	}
}

function clampPositionToViewport(left, top, panelWidth, panelHeight) {
	const maxLeft = window.innerWidth - panelWidth - VIEWPORT_MARGIN
	const maxTop = window.innerHeight - panelHeight - VIEWPORT_MARGIN
	return {
		left: Math.max(VIEWPORT_MARGIN, Math.min(left, Math.max(VIEWPORT_MARGIN, maxLeft))),
		top: Math.max(VIEWPORT_MARGIN, Math.min(top, Math.max(VIEWPORT_MARGIN, maxTop))),
	}
}

function applyPanelPosition(panel, left, top) {
	const rect = panel.getBoundingClientRect()
	const clamped = clampPositionToViewport(left, top, rect.width, rect.height)
	// Once positioned manually we switch from `right: 12px` to absolute
	// left/top so further drags can move freely. Setting `right: auto`
	// detaches the panel from the top-right anchor it had on first show.
	panel.style.left = clamped.left + 'px'
	panel.style.top = clamped.top + 'px'
	panel.style.right = 'auto'
	return clamped
}

function installHeaderDrag(panel, header) {
	let dragging = false
	let pointerStart = null
	let panelStart = null

	const onPointerDown = (e) => {
		// Buttons inside the header shouldn't initiate a drag — let them
		// receive the click normally. `closest()` covers icons / spans
		// nested inside the button.
		const target = e.target
		if (target && target.closest && target.closest('button')) return
		// Only the primary button (mouse) or any touch contact.
		if (typeof e.button === 'number' && e.button !== 0) return
		const rect = panel.getBoundingClientRect()
		pointerStart = { x: e.clientX, y: e.clientY }
		panelStart = { left: rect.left, top: rect.top }
		dragging = true
		header.classList.add('is-dragging')
		// Use pointer capture so the move/up still fire even when the
		// cursor leaves the header rectangle during a fast drag.
		if (typeof header.setPointerCapture === 'function' && typeof e.pointerId === 'number') {
			try { header.setPointerCapture(e.pointerId) } catch (_) {}
		}
		e.preventDefault()
	}

	const onPointerMove = (e) => {
		if (!dragging) return
		const dx = e.clientX - pointerStart.x
		const dy = e.clientY - pointerStart.y
		applyPanelPosition(panel, panelStart.left + dx, panelStart.top + dy)
	}

	const onPointerUp = () => {
		if (!dragging) return
		dragging = false
		header.classList.remove('is-dragging')
		// Persist the final position so the panel stays put across
		// reloads — matches the sticky-open behaviour above.
		const rect = panel.getBoundingClientRect()
		writePersistedPosition(rect.left, rect.top)
	}

	header.addEventListener('pointerdown', onPointerDown)
	header.addEventListener('pointermove', onPointerMove)
	header.addEventListener('pointerup', onPointerUp)
	header.addEventListener('pointercancel', onPointerUp)
}

function setOpen(next) {
	if (state.open === next) return
	state.open = next
	if (next) {
		injectStyles()
		const panel = buildPanel()
		if (!panel.parentNode) document.body.appendChild(panel)
		// Restore last-dragged position (if any). Done AFTER append so
		// getBoundingClientRect can measure the actual panel size for
		// clamp-to-viewport math.
		const stored = readPersistedPosition()
		if (stored) applyPanelPosition(panel, stored.left, stored.top)
		// Restore last fullscreen mode (sticky across reloads, same
		// rationale as `lay-dev-mode-open_v1`).
		if (readPersistedFullscreen()) setFullscreen(true)
		startRenderLoop()
	} else {
		stopRenderLoop()
		if (state.panel && state.panel.parentNode) {
			state.panel.parentNode.removeChild(state.panel)
		}
	}
	// Sticky: persist the toggle so the overlay stays on across reloads.
	// Explicit close clears the flag.
	writePersistedOpen(next)
}

function onKeyDown(event) {
	// Only top-level `D` with no modifiers. Lets browser shortcuts
	// (`Cmd+D` bookmark, etc.) through, and won't fire while typing.
	if (event.key !== 'd' && event.key !== 'D') return
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
	if (isTypingTarget(event.target)) return
	event.preventDefault()
	setOpen(!state.open)
}

// =============================================================================
// Physics body visualiser
//
// Reads `window.layDragPhysicsController.getDebugInfo()` on every RAF and
// draws one outline per:
//   • RED solid border:       Matter.js body's overall bounds (where the body
//                             collides — this is the actual physics shape).
//   • BLUE dashed border:     compound body parts (for line-mode text physics,
//                             one part per line of glyphs).
//   • GREEN dotted border:    restrain area (the walls the body bounces off).
//   • YELLOW dashed border:   the visible DOM element (twin / col).
//
// Use case: diagnose "the bouncing area looks too big" reports — if the body
// (red) is much smaller than the visible element (yellow), then the user is
// seeing empty container space around a glyph-tight body. If they're the same
// size, the body uses the whole element rect (whole-mode physics).
// =============================================================================

function readPersistedPhysicsOverlay() {
	try {
		return window.localStorage && window.localStorage.getItem(PHYSICS_OVERLAY_KEY) === '1'
	} catch (e) {
		return false
	}
}

function writePersistedPhysicsOverlay(value) {
	try {
		if (!window.localStorage) return
		if (value) window.localStorage.setItem(PHYSICS_OVERLAY_KEY, '1')
		else window.localStorage.removeItem(PHYSICS_OVERLAY_KEY)
	} catch (e) {}
}

function updatePhysicsButton(btn) {
	if (!btn) return
	btn.textContent = state.physicsOverlayEnabled ? 'Physics ✓' : 'Physics'
	btn.classList.toggle('active', !!state.physicsOverlayEnabled)
}

function ensurePhysicsOverlayContainer(parent) {
	const desiredParent = parent || document.body
	// If we already have an overlay element but it's attached to a
	// different parent (scope changed, e.g. user opened a project
	// overlay), move it. Body.bounds are in the scroll container's
	// content-coord space, so the overlay must live inside that
	// container to scroll naturally with the visible content.
	if (state.physicsOverlayEl) {
		if (state.physicsOverlayEl.parentNode !== desiredParent) {
			if (state.physicsOverlayEl.parentNode) state.physicsOverlayEl.parentNode.removeChild(state.physicsOverlayEl)
			desiredParent.appendChild(state.physicsOverlayEl)
		}
		return state.physicsOverlayEl
	}
	let el = document.getElementById(PHYSICS_OVERLAY_ID)
	if (!el) {
		// SVG host so we can draw rotated polygon outlines (the actual
		// collision shapes per body part). A plain div with CSS border
		// can only draw axis-aligned rectangles, which is wrong when the
		// body is rotated. Cover the full document/scroll-container so
		// the inner coords map 1:1 to body coords.
		el = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
		el.id = PHYSICS_OVERLAY_ID
		el.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
		// `overflow: visible` so polygons drawn at edge coords aren't clipped.
		// Width/height are set per-frame to match the scroll container's
		// scrollWidth/scrollHeight.
		el.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none;z-index:2147483646;overflow:visible;'
		el.setAttribute('width', '0')
		el.setAttribute('height', '0')
		desiredParent.appendChild(el)
	}
	state.physicsOverlayEl = el
	return el
}

function clearPhysicsOverlay() {
	if (state.physicsOverlayEl) {
		state.physicsOverlayEl.textContent = ''
	}
}

function renderPhysicsOverlayFrame() {
	state.physicsOverlayRaf = 0
	if (!state.physicsOverlayEnabled) return
	const controller = (typeof window !== 'undefined') && window.layDragPhysicsController
	if (!controller || typeof controller.getDebugInfo !== 'function') {
		// Controller not ready yet (or no drag-physics on this page). Try
		// again next frame so the overlay starts working the moment
		// physics binds its first body.
		state.physicsOverlayRaf = requestAnimationFrame(renderPhysicsOverlayFrame)
		return
	}
	const infos = controller.getDebugInfo() || []

	// Group bodies by their scroll container — we attach a separate
	// debug-overlay element inside each container so the outlines scroll
	// with the visible content. With a single container per nav cycle in
	// practice (everything in body, OR everything in the project overlay),
	// the first body's container wins. Mixed scopes would need separate
	// overlays per container; if that case shows up we add it then.
	const scrollContainer = (infos.length > 0 && infos[0].scrollContainer) || document.body
	const container = ensurePhysicsOverlayContainer(scrollContainer)
	// SVG needs an explicit width/height — polygons drawn outside the
	// SVG's box are clipped. Stretch to the scroll container's full
	// scrollable content size so outlines anywhere on the page are
	// visible. Cheap to set per frame; no layout cost.
	const svgW = (scrollContainer === document.body)
		? Math.max(document.documentElement.scrollWidth, document.body.scrollWidth)
		: scrollContainer.scrollWidth
	const svgH = (scrollContainer === document.body)
		? Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
		: scrollContainer.scrollHeight
	container.setAttribute('width', String(svgW))
	container.setAttribute('height', String(svgH))

	// Persistent-DOM: reuse existing child elements to avoid the 60 FPS
	// "create + destroy" churn.
	let row = 0
	for (let i = 0; i < infos.length; i++) {
		const info = infos[i]
		const rectScrollX = info.scrollContainer ? info.scrollContainer.scrollLeft : (window.scrollX || window.pageXOffset || 0)
		const rectScrollY = info.scrollContainer ? info.scrollContainer.scrollTop : (window.scrollY || window.pageYOffset || 0)
		// 1) Restrain area — dotted green. The walls the body bounces off.
		if (info.restrainArea) {
			row = applyPhysicsOverlayRect(container, row, info.restrainArea.minX, info.restrainArea.minY, info.restrainArea.maxX - info.restrainArea.minX, info.restrainArea.maxY - info.restrainArea.minY, '#1bd96a', '2 4', 1, 'restrain area')
		}
		// 2) DOM element rect — yellow dashed (the visible col / twin).
		if (info.elRect && info.elRect.width > 0) {
			row = applyPhysicsOverlayRect(container, row, info.elRect.left + rectScrollX, info.elRect.top + rectScrollY, info.elRect.width, info.elRect.height, '#f6c500', '6 4', 1, 'element rect')
		}
		// 3) Body broad-phase bounds — thin grey dashed. NOT where the
		//    collision actually happens, only the axis-aligned bounding
		//    rect Matter uses for broad-phase rejection. Faded out so it
		//    doesn't draw the eye — the real collision shapes are below.
		if (info.bodyBounds) {
			row = applyPhysicsOverlayRect(container, row, info.bodyBounds.minX, info.bodyBounds.minY, info.bodyBounds.maxX - info.bodyBounds.minX, info.bodyBounds.maxY - info.bodyBounds.minY, '#888', '3 3', 1, 'body bounds (broad-phase)')
		}
		// 4) Actual collision shapes — solid red polygons per part. These
		//    are what Matter narrow-phase collides against. For a rotated
		//    body they rotate with it; for a compound body each part is
		//    drawn separately (one per text line in line-mode physics).
		if (info.parts && info.parts.length > 0) {
			for (let j = 0; j < info.parts.length; j++) {
				const p = info.parts[j]
				if (p.vertices && p.vertices.length >= 3) {
					row = applyPhysicsOverlayPolygon(container, row, p.vertices, '#ff3333', 2, 'collision part ' + (j + 1))
				} else if (p.bounds) {
					// Fallback for parts without vertices (shouldn't happen for Matter rectangles).
					row = applyPhysicsOverlayRect(container, row, p.bounds.minX, p.bounds.minY, p.bounds.maxX - p.bounds.minX, p.bounds.maxY - p.bounds.minY, '#ff3333', '', 2, 'collision part ' + (j + 1))
				}
			}
		}
	}
	// Trim leftover children from a previous frame.
	while (container.children.length > row) {
		container.removeChild(container.lastChild)
	}

	state.physicsOverlayRaf = requestAnimationFrame(renderPhysicsOverlayFrame)
}

const SVG_NS = 'http://www.w3.org/2000/svg'

function applyPhysicsOverlayRect(container, rowIndex, x, y, w, h, color, dashArray, strokeWidth, label) {
	let el = container.children[rowIndex]
	if (!el || el.tagName !== 'rect') {
		if (el) container.removeChild(el)
		el = document.createElementNS(SVG_NS, 'rect')
		el.setAttribute('fill', 'none')
		container.insertBefore(el, container.children[rowIndex] || null)
	}
	el.setAttribute('x', String(x))
	el.setAttribute('y', String(y))
	el.setAttribute('width', String(Math.max(0, w)))
	el.setAttribute('height', String(Math.max(0, h)))
	el.setAttribute('stroke', color)
	el.setAttribute('stroke-width', String(strokeWidth))
	if (dashArray) el.setAttribute('stroke-dasharray', dashArray)
	else el.removeAttribute('stroke-dasharray')
	el.setAttribute('data-label', label)
	return rowIndex + 1
}

function applyPhysicsOverlayPolygon(container, rowIndex, vertices, color, strokeWidth, label) {
	let el = container.children[rowIndex]
	if (!el || el.tagName !== 'polygon') {
		if (el) container.removeChild(el)
		el = document.createElementNS(SVG_NS, 'polygon')
		el.setAttribute('fill', 'none')
		container.insertBefore(el, container.children[rowIndex] || null)
	}
	const pts = vertices.map((v) => v.x + ',' + v.y).join(' ')
	el.setAttribute('points', pts)
	el.setAttribute('stroke', color)
	el.setAttribute('stroke-width', String(strokeWidth))
	el.setAttribute('data-label', label)
	return rowIndex + 1
}

function setPhysicsOverlayEnabled(next) {
	state.physicsOverlayEnabled = !!next
	writePersistedPhysicsOverlay(state.physicsOverlayEnabled)
	if (state.panel) updatePhysicsButton(state.panel.querySelector('.lay-dev-mode-physics-bodies'))
	if (state.physicsOverlayEnabled) {
		ensurePhysicsOverlayContainer()
		if (!state.physicsOverlayRaf) {
			state.physicsOverlayRaf = requestAnimationFrame(renderPhysicsOverlayFrame)
		}
	} else {
		if (state.physicsOverlayRaf) {
			cancelAnimationFrame(state.physicsOverlayRaf)
			state.physicsOverlayRaf = 0
		}
		clearPhysicsOverlay()
		if (state.physicsOverlayEl && state.physicsOverlayEl.parentNode) {
			state.physicsOverlayEl.parentNode.removeChild(state.physicsOverlayEl)
			state.physicsOverlayEl = null
		}
	}
}

function openOnce() {
	// Body may not exist yet when the import resolves. Defer until DOM
	// is ready so `document.body.appendChild` always lands.
	if (document.body) {
		setOpen(true)
		return
	}
	document.addEventListener('DOMContentLoaded', () => setOpen(true), { once: true })
}

function init() {
	if (state.enabled) return
	const allowed = isActivationAllowed()
	// Log activation result so a quiet failure is debuggable from the
	// browser console without having to re-build.
	try {
		// eslint-disable-next-line no-console
		console.info('[lay-dev-mode] init — allowed:', allowed, '| layData.isAdmin:', window.layData && window.layData.isAdmin, '| ?lay-dev:', new URLSearchParams(window.location.search).get(URL_PARAM))
	} catch (_) {}
	if (!allowed) return
	state.enabled = true
	// Collectors run as soon as activation is allowed, NOT only while
	// the panel is open. That way events that fire during page boot
	// (e.g. `newpageshown`, lifecycle phase transitions) are captured
	// even if the user toggles the overlay open a moment later — or
	// when the sticky state auto-opens it right after collectors are
	// installed.
	installCollectors()
	window.addEventListener('keydown', onKeyDown, true)
	// Restore sticky state: if the overlay was open before the reload,
	// re-open it now so Dev Mode survives page navigation.
	if (readPersistedOpen()) {
		openOnce()
	}
	// Same sticky behaviour for the physics-body visualiser — survives
	// AJAX nav + hard reload so you can keep the outlines visible while
	// scrolling around / opening overlays.
	if (readPersistedPhysicsOverlay()) {
		setPhysicsOverlayEnabled(true)
	}
}

// Expose a tiny debug handle so the overlay can be toggled from the
// browser console even if the `D`-key path is blocked (some host
// keyboard layouts intercept letter shortcuts; some plugins swallow
// keydown via `stopPropagation` before our capture listener fires).
function ensureDebugHandle() {
	if (typeof window === 'undefined') return
	window.layDevMode = {
		open: () => setOpen(true),
		close: () => setOpen(false),
		toggle: () => setOpen(!state.open),
		state: () => ({ enabled: state.enabled, open: state.open }),
	}
}

// Run as soon as the script is loaded — `app.js` imports run after DOM
// content load on WP frontends, so the body exists. The actual panel is
// only appended on first toggle anyway.
if (typeof window !== 'undefined') {
	ensureDebugHandle()
	init()
}

export default { init }
